import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { adminRebuildStatic, adminRebuildStaticStatus } from "../lib/api";

// "Publish" / rebuild the full static site (all prerendered pages) on demand,
// so content + tagging changes go live for crawlers WITHOUT a redeploy.
// Content edits already auto-trigger a debounced rebuild; this button is the
// manual safety-net (e.g. after bulk tagging) + a live status readout.
export default function RebuildStaticButton() {
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState(null);
  const poll = useRef(null);

  const fetchStatus = async () => {
    try {
      const s = await adminRebuildStaticStatus();
      setStatus(s);
      setRunning(!!s.running);
      if (!s.running && poll.current) {
        clearInterval(poll.current);
        poll.current = null;
      }
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    fetchStatus();
    return () => poll.current && clearInterval(poll.current);
  }, []);

  const startPolling = () => {
    if (poll.current) clearInterval(poll.current);
    poll.current = setInterval(fetchStatus, 2500);
  };

  const onClick = async () => {
    try {
      setRunning(true);
      const r = await adminRebuildStatic();
      if (r.already_running) toast.info("rebuild already in progress");
      else toast.success("rebuilding static pages…");
      startPolling();
    } catch (e) {
      setRunning(false);
      toast.error(e?.response?.data?.detail || "could not start rebuild");
    }
  };

  const lastOk = status && status.last_ok;
  const files = status && status.last_files;
  const ago =
    status && status.last_finished
      ? `${Math.max(0, Math.round(Date.now() / 1000 - status.last_finished))}s ago`
      : null;

  return (
    <span className="inline-flex items-center gap-1.5" data-testid="rebuild-static">
      <button
        className="tv-cat-pill"
        onClick={onClick}
        disabled={running}
        title="Regenerate all static SEO pages from live data — no redeploy needed"
        data-testid="rebuild-static-btn"
      >
        [{running ? "rebuilding…" : "rebuild static"}]
      </button>
      {status && !running && (
        <span
          className={`text-[11px] ${lastOk === false ? "text-red-600" : "text-[#888]"}`}
          data-testid="rebuild-static-status"
        >
          {lastOk === false
            ? `failed: ${status.last_error || "error"}`
            : files != null
              ? `${files} pages${ago ? ` · ${ago}` : ""}`
              : ""}
        </span>
      )}
    </span>
  );
}
